<template>
  <div class="wa-provisioning-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader pageTitle="WhatsApp Provisioning" />

      <main class="page-content">
        <div class="wa-provisioning-view">

          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">🔌 WhatsApp Provisioning</h1>
              <p class="page-subtitle">Manage WhatsApp connections for all users</p>
            </div>
            <div class="header-stats" v-if="!loading">
              <div class="stat-card">
                <div class="stat-number">{{ stats.total }}</div>
                <div class="stat-label">Total Users</div>
              </div>
              <div class="stat-card stat-card--green">
                <div class="stat-number">{{ stats.connected }}</div>
                <div class="stat-label">Connected</div>
              </div>
              <div class="stat-card stat-card--red">
                <div class="stat-number">{{ stats.not_connected }}</div>
                <div class="stat-label">Not Connected</div>
              </div>
            </div>
          </div>

          <!-- Webhook Check -->
          <div class="webhook-check-bar">
            <button @click="checkWebhooks()" class="webhook-check-btn" :disabled="webhookCheck.loading">
              {{ webhookCheck.loading ? '⏳ Checking...' : '🔍 Check All Webhooks' }}
            </button>
            <button @click="checkWebhooks('self')" class="webhook-check-btn webhook-check-self" :disabled="webhookCheck.loading">
              {{ webhookCheck.loading ? '⏳ Checking...' : '🔍 Check My Account' }}
            </button>
            <span v-if="webhookCheck.result" class="webhook-summary">
              ✅ {{ webhookCheck.result.summary.match }} match &nbsp;
              ⚠️ {{ webhookCheck.result.summary.mismatch }} mismatch &nbsp;
              ❌ {{ webhookCheck.result.summary.not_set }} not set &nbsp;
              🔴 {{ webhookCheck.result.summary.api_error }} API errors
            </span>
            <span v-if="webhookCheck.error" class="webhook-error">{{ webhookCheck.error }}</span>
          </div>

          <!-- Webhook Results Table -->
          <div v-if="webhookCheck.result" class="webhook-results">
            <table class="webhook-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Setup</th>
                  <th>Phone ID</th>
                  <th>Status</th>
                  <th>Phone-level webhook (override)</th>
                  <th>App-level webhook (fallback)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in webhookCheck.result.users" :key="u.user_id"
                    :class="{ 'row-ok': u.uri_status === 'match', 'row-warn': u.uri_status === 'mismatch' || u.uri_status === 'not_set', 'row-err': u.graph_error }">
                  <td>{{ u.name }}<br><small>{{ u.email }}</small></td>
                  <td><code>{{ u.setup_method }}</code></td>
                  <td><code>{{ u.phone_id }}</code></td>
                  <td>
                    <span v-if="u.uri_status === 'match'" class="badge badge-ok">✅ Match</span>
                    <span v-else-if="u.uri_status === 'mismatch'" class="badge badge-warn">⚠️ Mismatch</span>
                    <span v-else-if="u.uri_status === 'not_set'" class="badge badge-warn">❌ Not Set</span>
                    <span v-else-if="u.graph_error" class="badge badge-err">🔴 API Error</span>
                  </td>
                  <td>
                    <small v-if="u.phone_webhook_url">{{ u.phone_webhook_url }}</small>
                    <small v-else-if="u.graph_error" class="text-red">{{ u.graph_error }}</small>
                    <small v-else class="text-muted">— not set —</small>
                  </td>
                  <td>
                    <small v-if="u.app_webhook_url" class="text-muted">{{ u.app_webhook_url }}</small>
                    <small v-else class="text-muted">—</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading users...</p>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="error-container">
            <div class="error-icon">⚠️</div>
            <h3>Error Loading Users</h3>
            <p>{{ error }}</p>
            <button @click="loadUsers" class="retry-button">Try Again</button>
          </div>

          <!-- Empty -->
          <div v-else-if="users.length === 0" class="empty-state">
            <div class="empty-icon">👥</div>
            <h3>No Users Found</h3>
            <p>No non-admin users exist yet.</p>
          </div>

          <!-- Table -->
          <div v-else class="table-container">
            <div class="table-header">
              <div class="table-header-left">
                <h2 class="table-title">Users ({{ filtered.length }})</h2>
                <!-- Filter tabs -->
                <div class="filter-tabs">
                  <button
                    v-for="tab in tabs" :key="tab.value"
                    class="filter-tab"
                    :class="{ active: activeTab === tab.value }"
                    @click="activeTab = tab.value"
                  >{{ tab.label }}</button>
                </div>
              </div>
              <div class="table-actions">
                <div class="search-box">
                  <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                  </svg>
                  <input v-model="search" type="text" placeholder="Search by name or email..." class="search-input" />
                </div>
                <button @click="loadUsers" class="refresh-button">🔄 Refresh</button>
              </div>
            </div>

            <!-- Desktop Table -->
            <div class="table-wrapper desktop-view">
              <table class="users-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Status</th>
                    <th>Phone Number</th>
                    <th>Verified Name</th>
                    <th>Quality</th>
                    <th>Setup Method</th>
                    <th>Connected At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(user, index) in filtered" :key="user.id" class="user-row">
                    <td class="index-cell">{{ index + 1 }}</td>
                    <td class="user-cell">
                      <div class="user-info">
                        <span class="user-name">{{ user.name }}</span>
                        <span class="user-email">{{ user.email }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="status-badge" :class="user.connected ? 'badge-green' : 'badge-red'">
                        {{ user.connected ? '✓ Connected' : '✗ Not Connected' }}
                      </span>
                    </td>
                    <td class="phone-cell">{{ user.phone_display_number || '—' }}</td>
                    <td>{{ user.phone_verified_name || '—' }}</td>
                    <td>
                      <span v-if="user.phone_quality_rating" class="status-badge" :class="qualityClass(user.phone_quality_rating)">
                        {{ user.phone_quality_rating }}
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td>
                      <span v-if="user.setup_method" class="method-badge" :class="user.setup_method === 'manual' ? 'method-manual' : 'method-embedded'">
                        {{ user.setup_method === 'manual' ? '🔧 Manual' : '🔗 Embedded' }}
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td class="date-cell">
                      <div v-if="user.connected_at" class="date-info">
                        <span class="date">{{ formatDate(user.connected_at) }}</span>
                        <span class="time">{{ formatTime(user.connected_at) }}</span>
                      </div>
                      <span v-else>—</span>
                    </td>
                    <td class="actions-cell">
                      <div class="action-buttons">
                        <button
                          @click="openProvisionModal(user)"
                          class="action-btn"
                          :class="user.connected ? 'reconnect-btn' : 'connect-btn'"
                          :title="user.connected ? 'Re-provision' : 'Connect WhatsApp'"
                        >
                          {{ user.connected ? '🔄' : '🔌' }}
                        </button>
                        <button
                          v-if="user.connected"
                          @click="checkWebhooks(user.id)"
                          class="action-btn webhook-btn"
                          :disabled="webhookCheck.loading"
                          title="Check webhook status"
                        >🔍</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile Cards -->
            <div class="mobile-view">
              <div v-for="user in filtered" :key="user.id" class="user-card">
                <div class="user-card-header">
                  <div class="user-name-info">
                    <h3 class="user-name">{{ user.name }}</h3>
                    <span class="user-email">{{ user.email }}</span>
                  </div>
                  <div class="card-actions">
                    <span class="status-badge" :class="user.connected ? 'badge-green' : 'badge-red'">
                      {{ user.connected ? '✓' : '✗' }}
                    </span>
                    <button
                      @click="openProvisionModal(user)"
                      class="mobile-action-btn"
                      :class="user.connected ? 'reconnect-btn' : 'connect-btn'"
                    >{{ user.connected ? '🔄' : '🔌' }}</button>
                  </div>
                </div>
                <div class="user-card-body" v-if="user.connected">
                  <div class="info-row">
                    <span class="info-label">Phone:</span>
                    <span class="info-value">{{ user.phone_display_number || '—' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Name:</span>
                    <span class="info-value">{{ user.phone_verified_name || '—' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Method:</span>
                    <span class="info-value">{{ user.setup_method || '—' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>

    <!-- Provision Modal -->
    <div v-if="modal.open" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>
            {{ modal.user && modal.user.connected ? '🔄 Re-provision' : '🔌 Connect' }}
            WhatsApp — {{ modal.user && modal.user.name }}
          </h3>
          <button @click="closeModal" class="close-button">✕</button>
        </div>

        <div class="modal-body">

          <!-- Already connected warning -->
          <div v-if="modal.user && modal.user.connected" class="info-banner warning-banner">
            ⚠️ This user is already connected (<strong>{{ modal.user.phone_display_number }}</strong>).
            Provisioning again will overwrite the existing connection.
          </div>

          <!-- System User Token Requirements -->
          <div class="requirements-box">
            <div class="requirements-title">📋 Before you proceed — ask the client to:</div>
            <ol class="requirements-list">
              <li>Go to <strong>Meta Business Manager</strong> → <em>Business Settings</em> → <em>System Users</em></li>
              <li>Create a <strong>System User</strong> and assign it to their WhatsApp Business Account with the <strong>MANAGE</strong> role</li>
              <li>Generate a <strong>Permanent Token</strong> with these permissions:
                <div class="perm-tags">
                  <span class="perm-tag">whatsapp_business_management</span>
                  <span class="perm-tag">whatsapp_business_messaging</span>
                  <span class="perm-tag">business_management</span>
                </div>
              </li>
              <li>Share that token with you to enter below</li>
            </ol>
          </div>

          <!-- Step 1: Select WABA -->
          <div class="detail-group">
            <h4>Step 1 — Select WhatsApp Business Account</h4>
            <div v-if="modal.loadingWabas" class="step-loading">
              <div class="loading-spinner small"></div> Loading accounts...
            </div>
            <div v-else-if="modal.wabaError" class="step-error">⚠️ {{ modal.wabaError }}</div>
            <div v-else>
              <select v-model="modal.selectedWaba" @change="onWabaSelected" class="form-select">
                <option value="">— Select a WABA —</option>
                <option v-for="waba in modal.wabas" :key="waba.id" :value="waba">
                  {{ waba.name }} ({{ waba.id }})
                </option>
              </select>
            </div>
          </div>

          <!-- Step 2: Phone Number -->
          <div class="detail-group" v-if="modal.selectedWaba">
            <h4>Step 2 — Phone Number</h4>

            <!-- Mode toggle -->
            <div class="phone-mode-toggle">
              <button
                class="mode-btn"
                :class="{ active: modal.phoneMode === 'existing' }"
                @click="modal.phoneMode = 'existing'"
              >📋 Select Existing</button>
              <button
                class="mode-btn"
                :class="{ active: modal.phoneMode === 'new' }"
                @click="modal.phoneMode = 'new'"
              >➕ Register New Number</button>
            </div>

            <!-- Existing phone -->
            <template v-if="modal.phoneMode === 'existing'">
              <div v-if="modal.loadingPhones" class="step-loading">
                <div class="loading-spinner small"></div> Loading phone numbers...
              </div>
              <div v-else-if="modal.phoneError" class="step-error">⚠️ {{ modal.phoneError }}</div>
              <div v-else-if="modal.phones.length === 0" class="step-empty">No phone numbers found for this WABA.</div>
              <div v-else>
                <!-- Phone list with status badges -->
                <div class="phone-list">
                  <div
                    v-for="phone in modal.phones"
                    :key="phone.id"
                    class="phone-list-item"
                    :class="{
                      selected: modal.selectedPhone && modal.selectedPhone.id === phone.id,
                      unverified: phone.code_verification_status !== 'VERIFIED'
                    }"
                    @click="onPhoneSelected(phone)"
                  >
                    <div class="phone-list-main">
                      <span class="phone-number-text">{{ phone.display_phone_number }}</span>
                      <span class="phone-name-text">{{ phone.verified_name }}</span>
                    </div>
                    <div class="phone-list-badges">
                      <span
                        class="verify-badge"
                        :class="phone.code_verification_status === 'VERIFIED' ? 'badge-green' : 'badge-red'"
                      >
                        {{ phone.code_verification_status === 'VERIFIED' ? '✓ Verified' : '✗ Not Verified' }}
                      </span>
                      <span v-if="phone.quality_rating" class="verify-badge" :class="qualityClass(phone.quality_rating)">
                        {{ phone.quality_rating }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- OTP flow for unverified existing phone -->
                <div v-if="modal.selectedPhone && modal.selectedPhone.code_verification_status !== 'VERIFIED'" class="otp-verify-box" style="margin-top:16px;">
                  <div class="info-banner warning-banner">
                    ⚠️ <strong>{{ modal.selectedPhone.display_phone_number }}</strong> is not yet verified.
                    You can verify it now or skip and do it later.
                  </div>

                  <!-- Send OTP -->
                  <div v-if="!modal.existingOtp.sent">
                    <div class="form-row" style="margin-top:12px;">
                      <div class="form-col-sm">
                        <label class="form-label">OTP Method</label>
                        <select v-model="modal.existingOtp.codeMethod" class="form-select">
                          <option value="SMS">SMS</option>
                          <option value="VOICE">Voice Call</option>
                        </select>
                      </div>
                      <div class="form-col" style="justify-content:flex-end;">
                        <button
                          class="otp-send-btn"
                          :disabled="modal.existingOtp.sending"
                          @click="sendExistingOtp"
                          style="margin-top:auto;"
                        >
                          <span v-if="modal.existingOtp.sending"><span class="btn-spinner"></span> Sending...</span>
                          <span v-else>📲 Send OTP</span>
                        </button>
                      </div>
                    </div>
                    <div v-if="modal.existingOtp.error" class="step-error">⚠️ {{ modal.existingOtp.error }}</div>
                  </div>

                  <!-- Verify OTP -->
                  <div v-else-if="!modal.existingOtp.verified">
                    <div class="info-banner success-banner" style="margin-top:12px;">
                      ✅ OTP sent via <strong>{{ modal.existingOtp.codeMethod }}</strong>
                    </div>
                    <div style="margin-top:12px;">
                      <label class="form-label">Enter OTP Code</label>
                      <div class="otp-input-row">
                        <input
                          v-model="modal.existingOtp.code"
                          type="text"
                          placeholder="6-digit code"
                          maxlength="8"
                          class="form-input otp-input"
                        />
                        <button
                          class="otp-verify-btn"
                          :disabled="modal.existingOtp.verifying || !modal.existingOtp.code"
                          @click="verifyExistingOtp"
                        >
                          <span v-if="modal.existingOtp.verifying"><span class="btn-spinner"></span></span>
                          <span v-else>Verify</span>
                        </button>
                      </div>
                      <div v-if="modal.existingOtp.otpError" class="step-error">⚠️ {{ modal.existingOtp.otpError }}</div>
                      <button class="resend-link" @click="resendExistingOtp" :disabled="modal.existingOtp.sending" style="margin-top:8px;">
                        {{ modal.existingOtp.sending ? 'Resending...' : '🔄 Resend OTP' }}
                      </button>
                    </div>
                  </div>

                  <!-- Verified -->
                  <div v-else class="info-banner success-banner" style="margin-top:12px;">
                    ✅ Phone number verified successfully!
                  </div>
                </div>
              </div>
            </template>

            <!-- Register new phone -->
            <template v-else>
              <div v-if="!modal.newPhone.otpSent">
                <div class="new-phone-form">
                  <div class="form-row">
                    <div class="form-col-sm">
                      <label class="form-label">Country Code</label>
                      <input v-model="modal.newPhone.cc" type="text" placeholder="e.g. 20" class="form-input" />
                    </div>
                    <div class="form-col">
                      <label class="form-label">Phone Number</label>
                      <input v-model="modal.newPhone.number" type="text" placeholder="e.g. 1234567890" class="form-input" />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-col">
                      <label class="form-label">Verified Name (Business Name)</label>
                      <input v-model="modal.newPhone.verifiedName" type="text" placeholder="e.g. My Business" class="form-input" />
                    </div>
                    <div class="form-col-sm">
                      <label class="form-label">OTP Method</label>
                      <select v-model="modal.newPhone.codeMethod" class="form-select">
                        <option value="SMS">SMS</option>
                        <option value="VOICE">Voice Call</option>
                      </select>
                    </div>
                  </div>
                  <div v-if="modal.newPhone.error" class="step-error">⚠️ {{ modal.newPhone.error }}</div>
                  <button
                    class="otp-send-btn"
                    :disabled="modal.newPhone.adding"
                    @click="addAndSendOtp"
                  >
                    <span v-if="modal.newPhone.adding"><span class="btn-spinner"></span> Sending OTP...</span>
                    <span v-else>📲 Add Number & Send OTP</span>
                  </button>
                </div>
              </div>

              <!-- OTP verification -->
              <div v-else class="otp-verify-box">
                <div class="info-banner success-banner">
                  ✅ OTP sent via <strong>{{ modal.newPhone.codeMethod }}</strong> to
                  <strong>+{{ modal.newPhone.cc }}{{ modal.newPhone.number }}</strong>
                </div>
                <div v-if="!modal.newPhone.otpVerified">
                  <label class="form-label">Enter OTP Code</label>
                  <div class="otp-input-row">
                    <input
                      v-model="modal.newPhone.otpCode"
                      type="text"
                      placeholder="6-digit code"
                      maxlength="8"
                      class="form-input otp-input"
                    />
                    <button
                      class="otp-verify-btn"
                      :disabled="modal.newPhone.verifying || !modal.newPhone.otpCode"
                      @click="verifyOtp"
                    >
                      <span v-if="modal.newPhone.verifying"><span class="btn-spinner"></span></span>
                      <span v-else>Verify</span>
                    </button>
                  </div>
                  <div v-if="modal.newPhone.otpError" class="step-error">⚠️ {{ modal.newPhone.otpError }}</div>
                  <button class="resend-link" @click="resendOtp" :disabled="modal.newPhone.resending">
                    {{ modal.newPhone.resending ? 'Resending...' : '🔄 Resend OTP' }}
                  </button>
                </div>
                <div v-else class="info-banner success-banner">
                  ✅ Phone number verified! Phone ID: <span class="mono">{{ modal.newPhone.phoneNumberId }}</span>
                </div>
              </div>
            </template>
          </div>

          <!-- Summary -->
          <div class="detail-group" v-if="readyToProvision">
            <h4>Summary</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <label>User</label>
                <span>{{ modal.user && modal.user.name }}</span>
              </div>
              <div class="detail-item">
                <label>WABA</label>
                <span>{{ modal.selectedWaba && modal.selectedWaba.name }}</span>
              </div>
              <div class="detail-item">
                <label>Phone Number</label>
                <span>{{ provisionPhoneLabel }}</span>
              </div>
              <div class="detail-item">
                <label>Phone ID</label>
                <span class="mono">{{ provisionPhoneId }}</span>
              </div>
            </div>

            <!-- System User Token (required) -->
            <div class="token-field">
              <label class="form-label">
                Client System User Token
                <span class="token-badge token-badge--required">required</span>
              </label>
              <input
                v-model="modal.systemUserToken"
                type="password"
                placeholder="Permanent system user token with access to this WABA"
                class="form-input"
                :class="{ 'input-error': modal.tokenMissing }"
                autocomplete="off"
                @input="modal.tokenMissing = false"
              />
              <p class="token-hint">
                The client must create a System User in their Meta Business Manager, assign it to their WABA, and generate a permanent token.
                This token is stored as their WhatsApp credential.
              </p>
            </div>
          </div>

          <!-- Provision result -->
          <div v-if="modal.result" class="info-banner" :class="modal.result.success ? 'success-banner' : 'error-banner-inline'">
            <template v-if="modal.result.success">
              ✅ <strong>Provisioned successfully!</strong><br>
              Session: <span class="mono">{{ modal.result.session_id }}</span><br>
              Webhook: <span class="mono small">{{ modal.result.webhook_url }}</span><br>
              Session initialized: {{ modal.result.session_initialized ? 'Yes ✓' : 'No (will retry on next restart)' }}
            </template>
            <template v-else>
              ❌ {{ modal.result.message }}
            </template>
          </div>

        </div>

        <div class="modal-footer">
          <button
            v-if="!modal.result || !modal.result.success"
            @click="provision"
            :disabled="!readyToProvision || modal.provisioning"
            class="provision-btn"
          >
            <span v-if="modal.provisioning">
              <span class="btn-spinner"></span> Provisioning...
            </span>
            <span v-else>🔌 Provision Now</span>
          </button>
          <button @click="closeModal" class="close-btn">
            {{ modal.result && modal.result.success ? 'Done' : 'Cancel' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import DashboardService from '@/services/dashboard.js'

export default {
  name: 'WhatsAppProvisioningView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      users: [],
      loading: false,
      error: null,
      search: '',
      activeTab: 'all',
      tabs: [
        { label: 'All', value: 'all' },
        { label: '✓ Connected', value: 'connected' },
        { label: '✗ Not Connected', value: 'not_connected' }
      ],
      stats: { total: 0, connected: 0, not_connected: 0 },
      webhookCheck: { loading: false, error: null, result: null },
      modal: {
        open: false,
        user: null,
        loadingWabas: false,
        wabaError: null,
        wabas: [],
        selectedWaba: '',
        phoneMode: 'existing',
        loadingPhones: false,
        phoneError: null,
        phones: [],
        selectedPhone: '',
        newPhone: {
          cc: '',
          number: '',
          verifiedName: '',
          codeMethod: 'SMS',
          adding: false,
          error: null,
          otpSent: false,
          phoneNumberId: null,
          otpCode: '',
          verifying: false,
          otpError: null,
          otpVerified: false,
          resending: false
        },
        existingOtp: {
          codeMethod: 'SMS',
          sending: false,
          sent: false,
          code: '',
          verifying: false,
          verified: false,
          error: null,
          otpError: null
        },
        systemUserToken: '',
        tokenMissing: false,
        provisioning: false,
        result: null
      }
    }
  },

  computed: {
    filtered () {
      let list = this.users
      if (this.activeTab === 'connected') list = list.filter(u => u.connected)
      if (this.activeTab === 'not_connected') list = list.filter(u => !u.connected)
      if (this.search.trim()) {
        const q = this.search.toLowerCase()
        list = list.filter(u =>
          (u.name || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q)
        )
      }
      return list
    },
    readyToProvision () {
      if (this.modal.phoneMode === 'existing') return !!this.modal.selectedPhone
      return this.modal.newPhone.otpVerified && !!this.modal.newPhone.phoneNumberId
    },
    provisionPhoneId () {
      if (this.modal.phoneMode === 'existing') return this.modal.selectedPhone ? this.modal.selectedPhone.id : ''
      return this.modal.newPhone.phoneNumberId || ''
    },
    provisionPhoneLabel () {
      if (this.modal.phoneMode === 'existing') return this.modal.selectedPhone ? this.modal.selectedPhone.display_phone_number : ''
      return `+${this.modal.newPhone.cc}${this.modal.newPhone.number}`
    }
  },

  created () {
    this.loadUsers()
  },

  methods: {
    async checkWebhooks (userId = null) {
      this.webhookCheck = { loading: true, error: null, result: null }
      try {
        const res = await DashboardService.getWebhookStatus(userId)
        this.webhookCheck = { loading: false, error: null, result: res }
      } catch (err) {
        this.webhookCheck = { loading: false, error: err.message || 'Failed to check webhooks.', result: null }
      }
    },

    async loadUsers () {
      this.loading = true
      this.error = null
      try {
        const res = await DashboardService.getUsersWhatsAppStatus()
        this.users = res.users || []
        this.stats = { total: res.total, connected: res.connected, not_connected: res.not_connected }
      } catch (err) {
        this.error = err.message || 'Failed to load users.'
      } finally {
        this.loading = false
      }
    },

    existingOtpDefaults () {
      return {
        codeMethod: 'SMS',
        sending: false,
        sent: false,
        code: '',
        verifying: false,
        verified: false,
        error: null,
        otpError: null
      }
    },

    newPhoneDefaults () {
      return {
        cc: '',
        number: '',
        verifiedName: '',
        codeMethod: 'SMS',
        adding: false,
        error: null,
        otpSent: false,
        phoneNumberId: null,
        otpCode: '',
        verifying: false,
        otpError: null,
        otpVerified: false,
        resending: false
      }
    },

    async openProvisionModal (user) {
      this.modal = {
        open: true,
        user,
        loadingWabas: true,
        wabaError: null,
        wabas: [],
        selectedWaba: '',
        phoneMode: 'existing',
        loadingPhones: false,
        phoneError: null,
        phones: [],
        selectedPhone: '',
        newPhone: this.newPhoneDefaults(),
        existingOtp: this.existingOtpDefaults(),
        systemUserToken: '',
        tokenMissing: false,
        provisioning: false,
        result: null
      }
      try {
        const res = await DashboardService.getClientWhatsAppAccounts()
        this.modal.wabas = res.data.accounts || []
      } catch (err) {
        this.modal.wabaError = err.message || 'Failed to load WABAs.'
      } finally {
        this.modal.loadingWabas = false
      }
    },

    async onWabaSelected () {
      if (!this.modal.selectedWaba) return
      this.modal.selectedPhone = ''
      this.modal.phones = []
      this.modal.phoneError = null
      this.modal.newPhone = this.newPhoneDefaults()
      this.modal.existingOtp = this.existingOtpDefaults()
      this.modal.loadingPhones = true
      try {
        this.modal.phones = await DashboardService.getWabaPhoneNumbers(this.modal.selectedWaba.id)
      } catch (err) {
        this.modal.phoneError = err.message || 'Failed to load phone numbers.'
      } finally {
        this.modal.loadingPhones = false
      }
    },

    onPhoneSelected (phone) {
      this.modal.selectedPhone = phone
      this.modal.existingOtp = this.existingOtpDefaults()
    },

    async sendExistingOtp () {
      const otp = this.modal.existingOtp
      otp.error = null
      otp.sending = true
      try {
        await DashboardService.requestPhoneVerificationCode(this.modal.selectedPhone.id, otp.codeMethod)
        otp.sent = true
      } catch (err) {
        otp.error = err.message || 'Failed to send OTP.'
      } finally {
        otp.sending = false
      }
    },

    async verifyExistingOtp () {
      const otp = this.modal.existingOtp
      otp.otpError = null
      otp.verifying = true
      try {
        await DashboardService.verifyPhoneVerificationCode(this.modal.selectedPhone.id, otp.code)
        otp.verified = true
        // Update the phone's status locally so the badge updates too
        this.modal.selectedPhone.code_verification_status = 'VERIFIED'
      } catch (err) {
        otp.otpError = err.message || 'Verification failed. Check the code and try again.'
      } finally {
        otp.verifying = false
      }
    },

    async resendExistingOtp () {
      const otp = this.modal.existingOtp
      otp.sending = true
      try {
        await DashboardService.requestPhoneVerificationCode(this.modal.selectedPhone.id, otp.codeMethod)
      } catch (err) {
        otp.otpError = err.message || 'Failed to resend OTP.'
      } finally {
        otp.sending = false
      }
    },

    async addAndSendOtp () {
      const np = this.modal.newPhone
      if (!np.cc || !np.number || !np.verifiedName) {
        np.error = 'Country code, phone number, and verified name are required.'
        return
      }
      np.error = null
      np.adding = true
      try {
        const res = await DashboardService.addPhoneToWaba(
          this.modal.selectedWaba.id, np.cc, np.number, np.verifiedName, np.codeMethod
        )
        np.phoneNumberId = res.phone_number_id
        np.otpSent = true
      } catch (err) {
        np.error = err.message || 'Failed to add phone number.'
      } finally {
        np.adding = false
      }
    },

    async verifyOtp () {
      const np = this.modal.newPhone
      np.otpError = null
      np.verifying = true
      try {
        await DashboardService.verifyPhoneVerificationCode(np.phoneNumberId, np.otpCode)
        np.otpVerified = true
      } catch (err) {
        np.otpError = err.message || 'Verification failed. Check the code and try again.'
      } finally {
        np.verifying = false
      }
    },

    async resendOtp () {
      const np = this.modal.newPhone
      np.resending = true
      try {
        await DashboardService.requestPhoneVerificationCode(np.phoneNumberId, np.codeMethod)
      } catch (err) {
        np.otpError = err.message || 'Failed to resend OTP.'
      } finally {
        np.resending = false
      }
    },

    async provision () {
      if (!this.readyToProvision || !this.modal.user) return
      if (!this.modal.systemUserToken.trim()) {
        this.modal.tokenMissing = true
        return
      }
      this.modal.provisioning = true
      this.modal.result = null
      try {
        const res = await DashboardService.manualProvisionWhatsApp(
          this.modal.user.id,
          this.modal.selectedWaba.id,
          this.provisionPhoneId,
          this.modal.systemUserToken
        )
        this.modal.result = { success: true, ...res }
        // Refresh the table in background
        this.loadUsers()
      } catch (err) {
        this.modal.result = { success: false, message: err.message || 'Provisioning failed.' }
      } finally {
        this.modal.provisioning = false
      }
    },

    closeModal () {
      this.modal.open = false
    },

    qualityClass (rating) {
      if (rating === 'GREEN') return 'badge-green'
      if (rating === 'YELLOW') return 'badge-yellow'
      if (rating === 'RED') return 'badge-red'
      return 'badge-gray'
    },

    formatDate (d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },

    formatTime (d) {
      if (!d) return ''
      return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
/* Layout */
.wa-provisioning-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.wa-provisioning-view { background: var(--bg-secondary); }

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 32px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.header-stats { display: flex; gap: 16px; }

.stat-card {
  background: var(--bg-secondary);
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  text-align: center;
  min-width: 90px;
}

.stat-card--green { border-color: rgba(16,185,129,0.3); background: rgba(16,185,129,0.05); }
.stat-card--red   { border-color: rgba(239,68,68,0.3);  background: rgba(239,68,68,0.05); }

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.stat-card--green .stat-number { color: #059669; }
.stat-card--red   .stat-number { color: #dc2626; }

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Loading / Error / Empty */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-spinner.small {
  width: 18px;
  height: 18px;
  border-width: 2px;
  margin-bottom: 0;
}

@keyframes spin { to { transform: rotate(360deg); } }

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px;
  text-align: center;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.error-icon  { font-size: 48px; margin-bottom: 16px; }
.error-container h3 { color: var(--text-primary); margin: 0 0 8px; }
.error-container p  { color: var(--text-secondary); margin: 0 0 24px; }

.retry-button {
  background: #667eea;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
}
.retry-button:hover { background: #5a67d8; }

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px;
  text-align: center;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.empty-icon { font-size: 48px; margin-bottom: 16px; }
.empty-state h3 { color: var(--text-primary); margin: 0 0 8px; }
.empty-state p  { color: var(--text-secondary); margin: 0; }

/* Table container */
.table-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  gap: 16px;
  flex-wrap: wrap;
}

.table-header-left { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  white-space: nowrap;
}

.filter-tabs { display: flex; gap: 4px; }

.filter-tab {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-tab.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.filter-tab:hover:not(.active) { background: var(--bg-tertiary); }

.table-actions { display: flex; align-items: center; gap: 12px; }

.search-box { position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 10px; color: var(--text-secondary); pointer-events: none; }

.search-input {
  padding: 8px 12px 8px 34px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  width: 220px;
  outline: none;
  transition: border-color 0.2s;
}
.search-input:focus { border-color: #667eea; }

.refresh-button {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.refresh-button:hover { background: var(--bg-tertiary); }

/* Table */
.table-wrapper { overflow-x: auto; }
.desktop-view  { display: block; }
.mobile-view   { display: none; }

.users-table { width: 100%; border-collapse: collapse; }

.users-table th {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
  padding: 14px 12px;
  text-align: left;
  border-bottom: 1px solid var(--border-primary);
  white-space: nowrap;
}

.users-table td {
  padding: 14px 12px;
  border-bottom: 1px solid var(--border-primary);
  color: var(--text-primary);
  font-size: 14px;
  vertical-align: middle;
}

.user-row:hover { background: var(--bg-tertiary); }

.index-cell { color: var(--text-secondary); width: 36px; }

.user-info { display: flex; flex-direction: column; gap: 3px; }
.user-name  { font-weight: 600; color: var(--text-primary); }
.user-email { font-size: 12px; color: var(--text-secondary); }

.phone-cell { font-weight: 500; color: #667eea; }

.date-info { display: flex; flex-direction: column; gap: 2px; }
.date { font-weight: 500; font-size: 13px; }
.time { font-size: 12px; color: var(--text-secondary); }

.actions-cell .action-buttons { display: flex; gap: 8px; }

.action-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.connect-btn:hover    { background: #667eea; color: white; border-color: #667eea; }
.reconnect-btn:hover  { background: #f59e0b; color: white; border-color: #f59e0b; }
.webhook-btn:hover    { background: #0ea5e9; color: white; border-color: #0ea5e9; }
.webhook-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Status / quality badges */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-green  { background: rgba(16,185,129,0.1);  color: #059669; }
.badge-yellow { background: rgba(245,158,11,0.1);  color: #d97706; }
.badge-red    { background: rgba(239,68,68,0.1);   color: #dc2626; }
.badge-gray   { background: var(--bg-tertiary);    color: var(--text-secondary); }

.method-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.method-manual   { background: rgba(102,126,234,0.1); color: #667eea; }
.method-embedded { background: rgba(16,185,129,0.1);  color: #059669; }

/* Mobile cards */
.user-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.2s;
}

.user-card:hover { border-color: var(--border-secondary); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }

.user-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
}

.user-name-info { flex: 1; }
.user-card-header .user-name  { font-size: 15px; font-weight: 600; color: var(--text-primary); margin: 0 0 3px; display: block; }
.user-card-header .user-email { font-size: 12px; color: var(--text-secondary); }

.card-actions { display: flex; align-items: center; gap: 8px; }

.mobile-action-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 16px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.mobile-action-btn.connect-btn:hover   { background: #667eea; color: white; border-color: #667eea; }
.mobile-action-btn.reconnect-btn:hover { background: #f59e0b; color: white; border-color: #f59e0b; }

.user-card-body { padding: 14px 16px; }

.info-row { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 10px; }
.info-row:last-child { margin-bottom: 0; }
.info-label { font-size: 13px; font-weight: 600; color: var(--text-secondary); min-width: 60px; flex-shrink: 0; }
.info-value { font-size: 13px; color: var(--text-primary); word-break: break-word; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
  position: sticky;
  top: 0;
  background: var(--bg-primary);
  z-index: 1;
}

.modal-header h3 { margin: 0; color: var(--text-primary); font-size: 18px; font-weight: 600; }

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
}
.close-button:hover { background: var(--bg-tertiary); color: var(--text-primary); }

.modal-body { padding: 24px; flex: 1; }

.detail-group { margin-bottom: 28px; }
.detail-group:last-child { margin-bottom: 0; }

.detail-group h4 {
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-primary);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.detail-item { display: flex; flex-direction: column; gap: 4px; }

.detail-item label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.mono { font-family: monospace; font-size: 13px !important; }
.small { font-size: 11px !important; word-break: break-all; }

/* Phone list */
.phone-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}
.phone-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  background: var(--bg-secondary);
}
.phone-list-item:hover { border-color: #667eea; background: rgba(102,126,234,0.05); }
.phone-list-item.selected { border-color: #667eea; background: rgba(102,126,234,0.1); }
.phone-list-item.unverified { border-color: rgba(245,101,101,0.4); }
.phone-list-item.selected.unverified { border-color: #f56565; background: rgba(245,101,101,0.08); }
.phone-list-main { display: flex; flex-direction: column; gap: 2px; }
.phone-number-text { font-size: 14px; font-weight: 600; color: var(--text-primary); }
.phone-name-text { font-size: 12px; color: var(--text-secondary); }
.phone-list-badges { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
.verify-badge {
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

/* Phone mode toggle */
.phone-mode-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.mode-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.mode-btn.active {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  font-weight: 600;
}

/* New phone form */
.new-phone-form { display: flex; flex-direction: column; gap: 12px; }
.form-row { display: flex; gap: 12px; }
.form-col { flex: 1; display: flex; flex-direction: column; gap: 6px; }
.form-col-sm { flex: 0 0 120px; display: flex; flex-direction: column; gap: 6px; }
.form-label { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
.form-input {
  padding: 10px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s;
}
.form-input:focus { border-color: #667eea; }
.otp-send-btn {
  align-self: flex-start;
  padding: 10px 20px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.otp-send-btn:hover:not(:disabled) { background: #5a67d8; }
.otp-send-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* OTP verify */
.otp-verify-box { display: flex; flex-direction: column; gap: 12px; }
.otp-input-row { display: flex; gap: 8px; align-items: center; }
.otp-input { max-width: 160px; letter-spacing: 4px; font-size: 18px; text-align: center; }
.otp-verify-btn {
  padding: 10px 20px;
  background: #48bb78;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.otp-verify-btn:hover:not(:disabled) { background: #38a169; }
.otp-verify-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.resend-link {
  background: none;
  border: none;
  color: #667eea;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}
.resend-link:disabled { opacity: 0.5; cursor: not-allowed; }

.token-field {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.token-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  background: rgba(102, 126, 234, 0.12);
  color: #667eea;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}
.token-badge--required {
  background: rgba(245, 101, 101, 0.12);
  color: #f56565;
}
.input-error { border-color: #f56565 !important; }
.requirements-box {
  background: rgba(102, 126, 234, 0.07);
  border: 1px solid rgba(102, 126, 234, 0.25);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 4px;
}
.requirements-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
}
.requirements-list {
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary);
}
.requirements-list li strong { color: var(--text-primary); }
.requirements-list li em { color: var(--text-secondary); font-style: normal; }
.perm-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.perm-tag {
  padding: 2px 10px;
  background: rgba(102, 126, 234, 0.12);
  color: #667eea;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  font-family: monospace;
}

.token-hint {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0;
}

.form-select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s;
  cursor: pointer;
}
.form-select:focus { border-color: #667eea; }

.step-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 14px;
}

.step-error { color: #dc2626; font-size: 14px; }
.step-empty { color: var(--text-secondary); font-size: 14px; }

/* Banners */
.info-banner {
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
}

.warning-banner { background: rgba(245,158,11,0.1); color: #92400e; border: 1px solid rgba(245,158,11,0.3); }
.success-banner { background: rgba(16,185,129,0.1); color: #065f46; border: 1px solid rgba(16,185,129,0.3); }
.error-banner-inline { background: rgba(239,68,68,0.1); color: #7f1d1d; border: 1px solid rgba(239,68,68,0.3); }

/* Modal footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--border-primary);
}

.provision-btn {
  background: #667eea;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}
.provision-btn:hover:not(:disabled) { background: #5a67d8; }
.provision-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.close-btn {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.close-btn:hover { background: var(--bg-tertiary); }

/* Responsive */
@media (max-width: 1024px) {
  .page-header { flex-direction: column; gap: 20px; align-items: flex-start; }
  .header-stats { align-self: flex-end; }
  .table-header { flex-direction: column; gap: 12px; align-items: flex-start; }
  .table-header-left { flex-direction: column; align-items: flex-start; gap: 12px; }
  .table-actions { align-self: flex-end; }
}

@media (max-width: 768px) {
  .main-content { margin-left: 0; padding-top: 70px; }
  .page-content { padding: 12px; }
  .desktop-view { display: none; }
  .mobile-view  { display: block; padding: 16px; }
  .search-input { width: 160px; }
  .modal-content { margin: 10px; max-height: calc(100vh - 20px); }
  .modal-header, .modal-body, .modal-footer { padding: 16px; }
  .detail-grid { grid-template-columns: 1fr; }
}

/* Webhook Check */
.webhook-check-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.webhook-check-btn {
  padding: 8px 18px;
  background: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.webhook-check-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.webhook-check-btn:hover:not(:disabled) { background: #4338ca; }
.webhook-check-self { background: #0369a1; }
.webhook-check-self:hover:not(:disabled) { background: #0284c7; }
.webhook-summary { font-size: 13px; color: #374151; }
.webhook-error   { font-size: 13px; color: #dc2626; }
.webhook-results { margin-bottom: 24px; overflow-x: auto; }
.webhook-table   { width: 100%; border-collapse: collapse; font-size: 13px; }
.webhook-table th { background: #f3f4f6; padding: 8px 12px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb; }
.webhook-table td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
.webhook-table tr.row-ok   { background: #f0fdf4; }
.webhook-table tr.row-warn { background: #fffbeb; }
.webhook-table tr.row-err  { background: #fef2f2; }
.badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 600; }
.badge-ok   { background: #d1fae5; color: #065f46; }
.badge-warn { background: #fef3c7; color: #92400e; }
.badge-err  { background: #fee2e2; color: #991b1b; }
.text-red   { color: #dc2626; }
.text-muted { color: #9ca3af; }
</style>
